import React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";

import EditableTitle from "../../App/Common/Components/EditableTitle";
import { IClientPatch, IPatchAction } from "../../components/course-author/helpers/patches";
import LocaleSelector from "../../components/pages/CourseAuthor/LocaleSelector";
import { H1 } from "../../components/utils/Headings";
import SaveInProgress, { ISaveInProgressStateProps } from "../../components/utils/SaveInProgress";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import Spinner from "../../components/utils/Spinner";
import SubTopbar from "../../components/utils/SubTopbar";
import TopBar from "../../components/utils/TopBar";
import * as selectors from "../../selectors/selectors";
import { PreferredLocale } from "../../utils/locales";
import * as accountActions from "../Account/actions";
import { LockMessageWindow } from "../Common/Components/LockMessageWindow";
import { IState } from "../reducer";
import { ITabInformation, TabButtons, TabsContainer } from "../Tabs";
import { IScormPackage, RequestState } from "../types/common";
import * as actions from "./actions";
import {
  modifyCompletedExitTextPatch,
  modifyExpectedNumUsersPatch,
  modifyInProgressExitTextPatch,
  modifyScormPackageStatePatch,
  modifyScormPackageTitlePatch,
  modifyUseStrictModePatch,
  toggleAllowCourseReviewPatch,
  toggleIncludeBrandingPatch,
  toggleLockPackagePatch,
  toggleUseCustomCompletionExitTextPatch,
  toggleUseCustomInProgressExitTextPatch,
} from "./helpers/patches";
import Members from "./tabs/Members";
import Settings from "./tabs/Settings";
import Summary from "./tabs/Summary";
import { OpenPackageIcon, ClosedPackageIcon } from "../../components/utils/Icons/PackageIcon";
import styled from "styled-components";

enum ScormPackageOption {
  Summary = "summary",
  Settings = "settings",
  Members = "members",
}

const tabs: Array<ITabInformation<ScormPackageOption>> = [
  {
    queryParam: "summary",
    tabItem: ScormPackageOption.Summary,
    title: "Summary",
  },
  {
    queryParam: "settings",
    tabItem: ScormPackageOption.Settings,
    title: "Settings",
  },
  {
    queryParam: "members",
    tabItem: ScormPackageOption.Members,
    title: "Members",
  },
];

interface IScormPackageTabsProps {
  accountId: string;
  currentLocale: PreferredLocale;
  modifyCompletedExitText: (message: string) => void;
  modifyInProgressExitText: (message: string) => void;
  deleteMember: (memberId: string) => void;
  initialTab: ScormPackageOption;
  memberIds: string[];
  scormPackage: IScormPackage;
  setCurrentLocale: (locale: PreferredLocale) => void;
  courseName: string;
  modifyTitle: (text: string) => void;
  modifyExpNumUsers: (value: number | null) => void;
  toggleUseCustomCompletionExitText: (bool: boolean) => void;
  modifyUseStrictMode: (bool: boolean) => void;
  toggleIncludeBranding: (bool: boolean) => void;
  toggleLockPackage: (bool: boolean) => void;
  toggleUseCustomInProgressExitText: (bool: boolean) => void;
  toggleAllowCourseReview: (bool: boolean) => void;
  modifyState: (state: string) => void;
}

const DEFAULT_TITLE = "Untitled Scorm Package";

export const ScormPackageTitle = styled.ul`
  display: flex;
  padding: 0;
  align-items: baseline;
  margin-bottom: 0;
`;

const ScormPackagePageContent: React.FC<IScormPackageTabsProps> = props => {
  const [tab, setTab] = React.useState(props.initialTab);
  const setSelected = (selectedTab: ScormPackageOption) => () => setTab(selectedTab);
  const renderTab = () => {
    switch (tab) {
      case ScormPackageOption.Members:
        return <Members deleteMember={props.deleteMember} accountId={props.accountId} memberIds={props.memberIds} />;
      case ScormPackageOption.Settings:
        return (
          <Settings
            accountId={props.accountId}
            currentLocale={props.currentLocale}
            modifyCompletedExitText={props.modifyCompletedExitText}
            modifyInProgressExitText={props.modifyInProgressExitText}
            scormPackage={props.scormPackage}
            toggleUseCustomCompletionExitText={props.toggleUseCustomCompletionExitText}
            toggleUseCustomInProgressExitText={props.toggleUseCustomInProgressExitText}
            toggleAllowCourseReview={props.toggleAllowCourseReview}
            toggleIncludeBranding={props.toggleIncludeBranding}
            toggleLockPackage={props.toggleLockPackage}
            updateScormPackageState={props.modifyState}
            updateNumExpectedUsers={props.modifyExpNumUsers}
            modifyUseStrictMode={props.modifyUseStrictMode}
          />
        );
      default:
        return <Summary courseName={props.courseName} scormPackage={props.scormPackage} />;
    }
  };

  return (
    <>
      <H1>
        <LocaleSelector currentLocale={props.currentLocale} setLocale={props.setCurrentLocale} />
        <ScormPackageTitle>
          {props.scormPackage.state === "closed" ? <ClosedPackageIcon /> : <OpenPackageIcon />}
          <EditableTitle
            addTitle={props.modifyTitle}
            modifyTitle={props.modifyTitle}
            currentTitle={props.scormPackage.name}
            defaultTitle={DEFAULT_TITLE}
            isMainTitle={true}
          />
        </ScormPackageTitle>
      </H1>
      <TabsContainer>
        <TabButtons tabs={tabs} displayed={tab} setSelected={setSelected} />
        {renderTab()}
      </TabsContainer>
    </>
  );
};

type ScormPackageManagementStateProps = ISaveInProgressStateProps & IScormPackageManagementStateProps;

interface IScormPackageManagementStateProps {
  accountId: string;
  courseName: string;
  currentLocale: PreferredLocale;
  scormPackageId: string;
  scormPackage: IScormPackage | null;
  userName: string;
  memberIds: string[];
  organizationName: string;
}

interface IScormPackageManagementDispatchProps {
  deleteMember: (memberId: string) => void;
  patchScormPackage: (patch: IClientPatch<IPatchAction>) => void;
  setCurrentLocale: (locale: PreferredLocale) => void;
  mount: (packageId: string) => void;
  unmount: () => void;
}

type ScormPackageManagementProps = ScormPackageManagementStateProps &
  IScormPackageManagementDispatchProps &
  RouteComponentProps<{ scormPackageId: string }>;

const getInitialTab = (param: string): ScormPackageOption => ScormPackageOption[param] || ScormPackageOption.Summary;

export const ScormPackageManagementComponent: React.FC<ScormPackageManagementProps> = props => {
  const { scormPackage } = props;
  const params = new URLSearchParams(props.location.search);
  const [menuExpanded, setMenuExpanded] = React.useState(true);
  const toggleMenuExpanded = () => setMenuExpanded(!menuExpanded);
  const setSideMenuTabAsSelected = () => {
    const element = document.getElementById("scorm-link");
    element?.setAttribute("class", "selected");
  };

  React.useEffect(setSideMenuTabAsSelected, [props.firstFetch, props.match.params]);

  React.useEffect(() => {
    props.mount(props.match.params.scormPackageId);
    return props.unmount;
  }, [props.match.params.scormPackageId]);

  if (props.scormPackage === null) {
    return <Spinner data-testid="spinner" />;
  }

  const currentScormPackage = props.scormPackage;
  const modifyTitle = (title: string) => {
    if (title === currentScormPackage.name) {
      return;
    }

    const patch = modifyScormPackageTitlePatch(currentScormPackage, props.accountId, title);
    props.patchScormPackage(patch);
  };

  const modifyExpNumUsers = (value: number | null) => {
    const patch = modifyExpectedNumUsersPatch(currentScormPackage, props.accountId, value);

    props.patchScormPackage(patch);
  };

  const modifyState = (state: string) => {
    const patch = modifyScormPackageStatePatch(currentScormPackage, props.accountId, state);

    props.patchScormPackage(patch);
  };

  const toggleUseCustomInProgressExitText = (useCustomInProgressExitText: boolean) => {
    const patch = toggleUseCustomInProgressExitTextPatch(
      currentScormPackage,
      props.accountId,
      useCustomInProgressExitText
    );

    props.patchScormPackage(patch);
  };

  const toggleUseCustomCompletionExitText = (useCustomCompletedExitText: boolean) => {
    const patch = toggleUseCustomCompletionExitTextPatch(
      currentScormPackage,
      props.accountId,
      useCustomCompletedExitText
    );

    props.patchScormPackage(patch);
  };

  const modifyInProgressExitText = (message: string) => {
    const patch = modifyInProgressExitTextPatch(currentScormPackage, props.accountId, props.currentLocale, message);

    props.patchScormPackage(patch);
  };

  const modifyCompletedExitText = (message: string) => {
    const patch = modifyCompletedExitTextPatch(currentScormPackage, props.accountId, props.currentLocale, message);

    props.patchScormPackage(patch);
  };

  const modifyUseStrictMode = (useStrictMode: boolean) => {
    const patch = modifyUseStrictModePatch(currentScormPackage, props.accountId, useStrictMode);
    props.patchScormPackage(patch);
  };

  const toggleAllowCourseReview = (allowCourseReview: boolean) => {
    const patch = toggleAllowCourseReviewPatch(currentScormPackage, props.accountId, allowCourseReview);

    props.patchScormPackage(patch);
  };

  const toggleIncludeBranding = (includeBranding: boolean) => {
    const patch = toggleIncludeBrandingPatch(currentScormPackage, props.accountId, includeBranding);
    props.patchScormPackage(patch);
  };

  const toggleLockPackage = (lockPackage: boolean) => {
    const patch = toggleLockPackagePatch(currentScormPackage, props.accountId, lockPackage);
    props.patchScormPackage(patch);
  };

  return (
    <>
      <SaveInProgress firstFetch={props.firstFetch} inProgress={props.inProgress} />
      <TopBar showToggleMenu={true} onToggleMenuClick={toggleMenuExpanded} userName={props.userName} />
      <SideMenu
        expanded={menuExpanded}
        organizationName={props.organizationName}
        userName={props.userName}
        onClose={toggleMenuExpanded}
        isOpen={true}
      />
      <SubTopbar withSidebar={true} isOpen={true}>
        {scormPackage?.locked && <LockMessageWindow />}
        <ScormPackagePageContent
          accountId={props.accountId}
          modifyCompletedExitText={modifyCompletedExitText}
          modifyInProgressExitText={modifyInProgressExitText}
          courseName={props.courseName}
          currentLocale={props.currentLocale}
          deleteMember={props.deleteMember}
          initialTab={getInitialTab(params.get("tab") || "")}
          memberIds={props.memberIds}
          scormPackage={props.scormPackage}
          setCurrentLocale={props.setCurrentLocale}
          modifyTitle={modifyTitle}
          modifyState={modifyState}
          modifyExpNumUsers={modifyExpNumUsers}
          toggleUseCustomCompletionExitText={toggleUseCustomCompletionExitText}
          toggleUseCustomInProgressExitText={toggleUseCustomInProgressExitText}
          toggleAllowCourseReview={toggleAllowCourseReview}
          toggleIncludeBranding={toggleIncludeBranding}
          toggleLockPackage={toggleLockPackage}
          modifyUseStrictMode={modifyUseStrictMode}
        />
      </SubTopbar>
    </>
  );
};

const mapStateToProps = (state: IState): ScormPackageManagementStateProps => ({
  accountId: selectors.accountId(state),
  courseName: state.App.ScormPackageManagement.courseName || "Not named course",
  currentLocale: state.App.ScormPackageManagement.currentLocale,
  firstFetch: state.App.ScormPackageManagement.scormPackage === null,
  inProgress: state.App.ScormPackageManagement.patchScormPackageStatus === RequestState.PENDING,
  memberIds: state.App.ScormPackageManagement.memberIds,
  organizationName: selectors.organizationName(state),
  scormPackage: state.App.ScormPackageManagement.scormPackage,
  scormPackageId: state.App.ScormPackageManagement.scormPackageId || "",
  userName: selectors.accountName(state),
});

const mapDispatchToProps = (dispatch: Dispatch<IAction>): IScormPackageManagementDispatchProps => ({
  deleteMember: (memberId: string) => dispatch(accountActions.deleteAccount(memberId)),
  mount: (scormPackageId: string) => dispatch(actions.mount(scormPackageId)),
  patchScormPackage: (patch: IClientPatch<IPatchAction>) => dispatch(actions.patchScormPackage(patch)),
  setCurrentLocale: (locale: PreferredLocale) => dispatch(actions.setCurrentLocale(locale)),
  unmount: () => dispatch(actions.unmount()),
});

export const ScormPackageManagement = connect(mapStateToProps, mapDispatchToProps)(ScormPackageManagementComponent);
